import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {collection, query, orderBy, getFirestore, where, Timestamp, getDocs, deleteDoc, doc} from "firebase/firestore";
import {collectionData, Firestore} from "@angular/fire/firestore";
import {AppService} from "../../shared/services/app.service";
import {SiteService} from "../../shared/services/site.service";
import {AppConfirmService} from "../../shared/services/app-confirm/app-confirm.service";
import {ActivityTrace} from "../../shared/models/ActivityTrace.model";
import {Subscription} from "rxjs";

@Component({
    selector: 'app-activity',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div fxLayout="column" class="activity-view">
            <mat-card class="activity-hero m-0 mb-12 p-8">
                <div fxLayout="row wrap" fxLayoutAlign="space-between center" fxLayoutGap="16px">
                    <div>
                        <div class="activity-kicker">Admin</div>
                        <div class="activity-title">{{ headerTitle }}</div>
                        <div class="activity-subtitle">{{ headerSubtitle }}</div>
                    </div>
                    <div class="activity-stats" fxLayout="row" fxLayoutGap="12px">
                        <div class="activity-stat">
                            <div class="activity-stat-value">{{ traces.length }}</div>
                            <div class="activity-stat-label">Traces</div>
                        </div>
                        <div class="activity-stat" *ngIf="traces[0] as latest">
                            <div class="activity-stat-value">{{ latest.date | amTimeAgo }}</div>
                            <div class="activity-stat-label">Derniere activite</div>
                        </div>
                    </div>
                </div>
            </mat-card>

            <mat-card class="activity-filters m-0 mb-12 p-8">
                <div fxLayout="row wrap" fxLayoutAlign="space-between center" fxLayoutGap="16px" class="activity-filters-row p-8">
                    <div fxLayout="row wrap" fxLayoutAlign="start center" fxLayoutGap="12px" class="activity-filter-block">
                        <div class="activity-filter-title">Depuis</div>
                        <mat-button-toggle-group [value]="selectedRange" (change)="setRange($event.value)">
                            <mat-button-toggle *ngFor="let range of ranges" [value]="range.key">
                                {{ range.label }}
                            </mat-button-toggle>
                        </mat-button-toggle-group>
                    </div>

                    <div fxLayout="row wrap" fxLayoutAlign="start center" fxLayoutGap="12px" class="activity-filter-block">
                        <div class="activity-filter-title">Groupe</div>
                        <mat-button-toggle-group [value]="selectedUserGroup" (change)="setUserGroup($event.value)">
                            <mat-button-toggle [value]="'ALL'">
                                Tous
                            </mat-button-toggle>
                            <mat-button-toggle *ngFor="let group of userGroups" [value]="group">
                                {{ group }}
                            </mat-button-toggle>
                        </mat-button-toggle-group>
                    </div>
                </div>
            </mat-card>

            <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px" class="activity-body">
                <mat-card class="activity-users-panel m-0 p-8">
                    <div class="activity-users-head">
                        <div class="activity-filter-title">Utilisateurs</div>
                    </div>

                    <div class="activity-users-list">
                        <button mat-button class="activity-user-row"
                                [class.active]="selectedUserEmail === null"
                                (click)="clearSelectedUser()">
                            <span class="activity-user-row-name">Tous</span>
                            <span class="activity-user-row-count">{{ usersTotalCount }}</span>
                        </button>

                        <button mat-button class="activity-user-row"
                                *ngFor="let user of users; trackBy: trackByUser"
                                [class.active]="selectedUserEmail === user.email"
                                (click)="selectUser(user)">
                            <span class="activity-user-row-name">{{ user.name }}</span>
                            <span class="activity-user-row-count">{{ user.count }}</span>
                        </button>
                    </div>
                </mat-card>

                <div fxFlex>
                    <div class="activity-empty mat-card p-8" *ngIf="!traces.length">
                        Aucune trace chargee.
                    </div>

                    <div class="activity-list" fxLayout="column" fxLayoutGap="10px" *ngIf="traces.length">
                        <mat-card class="activity-item p-0"
                                  *ngFor="let tr of traces; let i = index; trackBy: trackByTrace"
                                  (click)="myapp.showConsole(tr)">
                            <div class="activity-item-inner" fxLayout="row" fxLayoutAlign="start stretch">
                                <div class="activity-index">
                                    <div>{{ i + 1 }}</div>
                                </div>

                                <div fxFlex class="activity-content" fxLayout="column">
                                    <div fxLayout="row wrap" fxLayoutAlign="space-between center" fxLayoutGap="8px" class="activity-head">
                                        <div class="activity-mainline" fxLayout="row wrap" fxLayoutAlign="start center" fxLayoutGap="8px">
                                            <mat-icon class="activity-icon">person</mat-icon>
                                            <span class="activity-route">{{ tr.user_group }} / {{ tr.userDisplay }}</span>
                                            <mat-icon class="activity-icon">{{ tr.icon }}</mat-icon>
                                            <span class="activity-route">{{ tr.routeDisplay }}</span>
                                            <mat-chip class="activity-chip activity-chip-type">{{ tr.activityTypeDisplay }}</mat-chip>
                                            <mat-chip class="activity-chip" *ngIf="tr.activity_arg2.type">{{ tr.activity_arg2.type }}</mat-chip>
                                        </div>

                                        <div class="activity-time" *ngIf="tr.date">
                                            <span>{{ tr.date | amTimeAgo }}</span>
                                            <small>{{ tr.date | date:'dd.MM.y HH:mm' }}</small>
                                        </div>
                                    </div>

                                    <div fxLayout="row wrap" fxLayoutGap="6px" class="activity-meta">
                                        <span class="activity-pill" *ngIf="tr.site_ref">
                                            <mat-icon>domain</mat-icon>
                                            {{ tr.site_ref }}
                                        </span>
                                        <span class="activity-pill" *ngIf="tr.browserDisplay">
                                            <mat-icon>language</mat-icon>
                                            {{ tr.browserDisplay }}
                                        </span>
                                        <span class="activity-pill" *ngIf="tr.osDisplay">
                                            <mat-icon>computer</mat-icon>
                                            {{ tr.osDisplay }}
                                        </span>
                                        <span class="activity-pill" *ngIf="tr.deviceTypeDisplay">
                                            <mat-icon>devices</mat-icon>
                                            {{ tr.deviceTypeDisplay }}
                                        </span>
                                        <span class="activity-pill" *ngIf="tr.app_ver">
                                            <mat-icon>commit</mat-icon>
                                            v{{ tr.app_ver }}
                                        </span>
                                        <span class="activity-arg2-inline" *ngIf="tr.activityArg2Display">
                                            {{ tr.activityArg2Display }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </mat-card>
                    </div>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./admin.component.scss']
})
export class ActivityComponent implements OnInit, OnDestroy {
    readonly allUserGroups = ['ENERPLAN', 'CLIENT', 'CANTON', 'AUTRE'];
    private didBootstrapGroupClick = false;
    traces: ActivityTraceView[] = [];
    rawTraces: ActivityTraceView[] = [];
    selectedRange: 'today' = 'today';
    selectedUserGroup = 'ENERPLAN';
    selectedUserEmail: string = null;
    selectedUserName: string = null;
    ranges = [
        {key: 'today', label: 'Ce matin'},
    ];
    userGroups: string[] = this.allUserGroups;
    users: ActivityUserSummary[] = [];
    usersTotalCount = 0;
    rangeLabelMap = {
        today: 'Depuis ce matin',
    };
    private tracesSubscription: Subscription = null;

    constructor(
        public myapp: AppService,
        public db: Firestore,
        public site: SiteService,
        public confirmService: AppConfirmService,
        private cdr: ChangeDetectorRef,
    ) {

    }


    ngOnInit(): void {
        this.myapp.storeCurrentRoute();
        this.loadTraces();
    }

    ngOnDestroy(): void {
        if (this.tracesSubscription) {
            this.tracesSubscription.unsubscribe();
        }
    }

    get headerTitle(): string {
        return this.selectedUserName
            ? 'Timeline de ' + this.selectedUserName
            : 'Journal d\'activite';
    }

    get headerSubtitle(): string {
        return this.selectedUserName
            ? 'Historique filtre pour cet utilisateur.'
            : this.rangeLabelMap[this.selectedRange] + ', trie par date decroissante.';
    }

    setRange(range: 'today'): void {
        if (!range || range === this.selectedRange) return;
        this.selectedRange = range;
        this.loadTraces();
    }

    setUserGroup(userGroup: string, force = false): void {
        if (!userGroup) return;
        if (!force && userGroup === this.selectedUserGroup) return;
        this.selectedUserGroup = userGroup;
        this.selectedUserEmail = null;
        this.selectedUserName = null;
        this.applyFilters();
    }

    selectUser(user: ActivityUserSummary): void {
        this.selectedUserEmail = user.email;
        this.selectedUserName = user.name;
        this.applyFilters();
    }

    selectUserFromTrace(trace: ActivityTraceView, event: MouseEvent): void {
        event.stopPropagation();
        this.selectedUserEmail = trace.user_email || null;
        this.selectedUserName = trace.userDisplay || null;
        this.applyFilters();
    }

    clearSelectedUser(): void {
        this.selectedUserEmail = null;
        this.selectedUserName = null;
        this.applyFilters();
    }

    loadTraces(): void {
        if (this.tracesSubscription) {
            this.tracesSubscription.unsubscribe();
            this.tracesSubscription = null;
        }

        const afs = getFirestore();
        const startDate = this.getRangeStartDate(this.selectedRange);
        const q = query(
            collection(afs, "activity"),
            where("ts", ">=", Timestamp.fromDate(startDate)),
            orderBy("ts", "desc"),
        );

        if (this.selectedRange === 'today') {
            this.tracesSubscription = collectionData(q, {idField: 'id'}).subscribe(docs => {
                this.handleDocs(docs);
            });
            return;
        }

        getDocs(q).then(snapshot => {
            const docs = snapshot.docs.map(doc => {
                return {id: doc.id, ...doc.data()};
            });
            this.handleDocs(docs);
        });
    }

    getRangeStartDate(range: 'today'): Date {
        const now = new Date();

        return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    }

    getIcon(activityType: string): string {
        const key = (activityType || '').toUpperCase();
        if (key.includes('NAVIGATE')) return 'route';
        if (key.includes('LOGIN') || key.includes('SIGNIN')) return 'login';
        if (key.includes('LOGOUT') || key.includes('SIGNOUT')) return 'logout';
        if (key.includes('DELETE')) return 'delete_outline';
        if (key.includes('UPDATE') || key.includes('EDIT')) return 'edit';
        if (key.includes('CREATE') || key.includes('ADD')) return 'add_circle_outline';
        if (key.includes('SHARE')) return 'share';
        if (key.includes('EXPORT') || key.includes('DOWNLOAD') || key.includes('UPLOAD')) return 'download';
        return 'bolt';
    }

    trackByTrace(index: number, trace: ActivityTraceView): string {
        return trace.id || trace.uid || String(index);
    }

    trackByUser(index: number, user: ActivityUserSummary): string {
        return user.email;
    }

    formatArg2(value: any): string {
        if (typeof value === 'string') return value;
        try {
            return JSON.stringify(value);
        } catch (e) {
            return String(value);
        }
    }

    toView(trace: ActivityTrace): ActivityTraceView {
        const browserDisplay = trace.deviceInfos?.browser
            ? trace.deviceInfos.browser + (trace.deviceInfos.browser_version ? ' ' + trace.deviceInfos.browser_version : '')
            : '';

        const activityArg2Display = trace.activity_arg2 !== undefined
        && trace.activity_arg2 !== null
        && trace.activity_arg2 !== ''
            ? this.formatArg2(trace.activity_arg2)
            : '';

        return {
            ...trace,
            icon: this.getIcon(trace.activity_type),
            userDisplay: trace.user_name || trace.user_email || 'Utilisateur inconnu',
            activityTypeDisplay: trace.activity_type || 'ACTION',
            routeDisplay: trace.activity_arg1 || '-',
            browserDisplay,
            osDisplay: trace.deviceInfos?.os || '',
            deviceTypeDisplay: trace.deviceInfos?.deviceType || '',
            activityArg2Display,
        };
    }

    handleDocs(docs: any[]): void {
        this.rawTraces = docs
            .map(doc => new ActivityTrace(doc))
            .filter(trace => !!trace.date)
            .map(trace => this.toView(trace));
        this.userGroups = this.allUserGroups;
        if (this.selectedUserGroup !== 'ALL' && !this.allUserGroups.includes(this.selectedUserGroup)) {
            this.selectedUserGroup = 'ALL';
        }

        // this.rawTraces.forEach(tr => {
        //     if (tr.user_email.includes("maddah"))
        //         deleteDoc(doc(getFirestore(), "activity", tr.id));
        // })
        if (!this.didBootstrapGroupClick) {
            this.didBootstrapGroupClick = true;
            this.setUserGroup('ENERPLAN', true);
            this.cdr.markForCheck();
            return;
        }

        this.applyFilters();
        this.cdr.markForCheck();
    }

    applyFilters(): void {
        let groupFiltered = this.rawTraces;

        if (this.selectedUserGroup !== 'ALL') {
            groupFiltered = groupFiltered.filter(trace => trace.user_group === this.selectedUserGroup);
        }

        this.users = this.buildUsersList(groupFiltered);
        this.usersTotalCount = groupFiltered.length;

        let filtered = groupFiltered;
        if (this.selectedUserEmail) {
            filtered = filtered.filter(trace => trace.user_email === this.selectedUserEmail);
        }

        if (this.selectedUserEmail && !this.users.some(user => user.email === this.selectedUserEmail)) {
            this.selectedUserEmail = null;
            this.selectedUserName = null;
            filtered = groupFiltered;
        }

        this.traces = filtered;
        this.cdr.markForCheck();
    }

    getTraceTime(trace: ActivityTraceView): number {
        return trace.date ? trace.date.getTime() : 0;
    }

    buildUsersList(traces: ActivityTraceView[]): ActivityUserSummary[] {
        const usersMap = new Map<string, ActivityUserSummary>();
        traces.forEach(trace => {
            const email = trace.user_email || trace.uid || 'unknown';
            if (!usersMap.has(email)) {
                usersMap.set(email, {
                    email,
                    name: trace.user_name || trace.user_email || 'Utilisateur inconnu',
                    count: 0,
                    lastTs: this.getTraceTime(trace),
                });
            }
            usersMap.get(email).count++;
            const traceTs = this.getTraceTime(trace);
            if (traceTs > usersMap.get(email).lastTs) {
                usersMap.get(email).lastTs = traceTs;
            }
        });

        return Array.from(usersMap.values())
            .sort((a, b) => b.lastTs - a.lastTs || a.name.localeCompare(b.name));
    }

}

interface ActivityTraceView {
    id: string;
    uid: string;
    activity_type: string;
    activity_arg1: string;
    activity_arg2: any;
    app_ver: string;
    app_ver_ts: number;
    deviceInfos: any;
    site_uid: string;
    site_ref: string;
    user_name: string;
    user_email: string;
    user_group: string;
    ts: any;
    date: Date;
    icon: string;
    userDisplay: string;
    activityTypeDisplay: string;
    routeDisplay: string;
    browserDisplay: string;
    osDisplay: string;
    deviceTypeDisplay: string;
    activityArg2Display: string;
}

interface ActivityUserSummary {
    email: string;
    name: string;
    count: number;
    lastTs: number;
}

/*
{
  "uid": "najib.maddah@gmail.com_2026031511_NAVIGATE_6w48in",
  "activity_arg2": "",
  "app_ver": "15036",
  "user_group": "ENERPLAN",
  "ts": {
    "seconds": 1773570591,
    "nanoseconds": 705000000
  },
  "site_uid": "622f4d3aec7e1f0004290b53",
  "user_name": "Najib Maddah",
  "deviceInfos": {
    "os": "Windows",
    "os_version": "windows-10",
    "deviceType": "desktop",
    "browser": "Firefox",
    "screenWidth": 3440,
    "isDesktopDevice": true,
    "device": "Unknown",
    "isTablet": false,
    "userAgent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:148.0) Gecko/20100101 Firefox/148.0",
    "browser_version": "148.0",
    "screenHeight": 1279,
    "isMobile": false,
    "orientation": "landscape"
  },
  "site_ref": "RenensPiscine",
  "activity_arg1": "/docs",
  "app_ver_ts": 1773568844831,
  "user_email": "najib.maddah@gmail.com",
  "activity_type": "NAVIGATE",
  "id": "najib.maddah@gmail.com_2026031511_NAVIGATE_6w48in"
}
 */
